import React, { useCallback } from 'react'
import LazyHydrate from 'react-lazy-hydration'
import { HorizontalBannerScroller } from '@/molecules/HorizontalBannerScroller'
import { useRecommendedHeroBanners } from '@/services/RecommendedBannerService'
import { useRecommendationTracking } from '@/services/RecommendedBannerService/useRecommendationTracking'
import { logger } from '@/utils/logging'
import { getObjectFromSessionStorage, writeToSessionStorage } from '@/utils/session-storage'
import { RecommendedBanner } from '@/views/WatchLandingView/RecommendedBannerCarousel/RecommendedBanner/RecommendedBanner'
import { RecommendedBannerCarouselPlaceholder } from '@/views/WatchLandingView/RecommendedBannerCarousel/RecommendedBannerCarouselPlaceholder'
import { RecommendedBannerCarouselFull } from './RecommendedBannerCarouselFull'
import { RecommendedBannerCarouselFullPlaceholder } from './RecommendedBannerCarouselFullPlaceholder'

const isNumericRegex = /^\d+$/

export const RecommendedBannerCarousel: React.FC = () => {
  const { heroBanners, loading } = useRecommendedHeroBanners()
  const { trackRecommendation } = useRecommendationTracking()

  const trackImpressions = useCallback(
    (currentSlide: number) => {
      const el = document.querySelector(`.slick-slide[data-index="${currentSlide}"] .recommended-banner`)
      const recommendationId = el?.getAttribute('data-recommendation-id')
      if (!recommendationId) {
        logger().error('Attempting to track impression for recommendation banner that has no id!')
        return
      }
      if (isNumericRegex.test(recommendationId)) return
      const trackedImpressions = getObjectFromSessionStorage<Record<string, string>>('recommendationBannerImpressions')
      if (!trackedImpressions || !trackedImpressions[recommendationId]) {
        trackRecommendation({ type: 'IMPRESSION', items: [{ id: recommendationId }] })
        writeToSessionStorage('recommendationBannerImpressions', {
          ...trackedImpressions,
          [recommendationId]: new Date(),
        })
      }
    },
    [trackRecommendation],
  )

  const trackFullImpressions = useCallback(
    (recommendationId: string) => {
      if (!recommendationId || isNumericRegex.test(recommendationId)) return
      const trackedImpressions = getObjectFromSessionStorage<Record<string, string>>('recommendationBannerImpressions')
      if (!trackedImpressions || !trackedImpressions[recommendationId]) {
        trackRecommendation({ type: 'IMPRESSION', items: [{ id: recommendationId }] })
        writeToSessionStorage('recommendationBannerImpressions', {
          ...trackedImpressions,
          [recommendationId]: new Date(),
        })
      }
    },
    [trackRecommendation],
  )

  if (loading) {
    return (
      <LazyHydrate whenVisible>
        <div>
          {/* Desktop placeholder */}
          <div className="hidden lg:block" data-testid="banners-placeholder">
            <RecommendedBannerCarouselFullPlaceholder />
          </div>
          {/* Mobile placeholder */}
          <div className="lg:hidden" data-testid="banners-placeholder">
            <RecommendedBannerCarouselPlaceholder />
          </div>
        </div>
      </LazyHydrate>
    )
  }

  return (
    <LazyHydrate whenVisible>
      <div>
        {/* Desktop version */}
        <div className="hidden lg:block">
          <RecommendedBannerCarouselFull heroBanners={heroBanners} trackImpressions={trackFullImpressions} />
        </div>
        {/* Mobile version */}
        <div className="lg:hidden">
          <HorizontalBannerScroller
            className="recommended-banner-carousel group-recommended-banner"
            centerMode
            centerPadding="32px"
            chevronClassName="group-recommended-banner-hover:block group-recommended-banner-hover:opacity-100"
            afterChange={trackImpressions}
          >
            {heroBanners?.map((banner) => (
              <RecommendedBanner key={banner.id} banner={banner} />
            ))}
          </HorizontalBannerScroller>
        </div>
      </div>
    </LazyHydrate>
  )
}
